import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Calendar, FileText, CreditCard, LogOut, Loader2 } from "lucide-react";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyInv, setBusyInv] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, i, p] = await Promise.all([
        api.get("/portal/bookings"),
        api.get("/portal/invoices"),
        api.get("/portal/payments"),
      ]);
      setBookings(b.data);
      setInvoices(i.data);
      setPayments(p.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const payInvoice = async (inv) => {
    setBusyInv(inv.id);
    try {
      const { data } = await api.post("/checkout/invoice", { invoice_id: inv.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      setBusyInv(null);
    }
  };

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="client-portal">
      <header className="border-b border-[var(--border)] glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="block">
              <img src="/phm-logo.png" alt="PHM" className="w-9 h-9 object-cover border border-[var(--border)] hover:border-[var(--red)] transition-colors" />
            </Link>
            <div>
              <div className="font-display text-sm font-semibold">Client Portal</div>
              <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.2em]">{user?.email}</div>
            </div>
          </div>
          <button onClick={doLogout} className="text-sm text-[var(--text-2)] hover:text-white flex items-center gap-1.5" data-testid="portal-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mb-2">Welcome, {user?.name}.</h1>
        <p className="text-[var(--text-2)] mb-8">Your bookings, invoices, and payments in one place.</p>

        <div className="grid grid-cols-3 gap-4 mb-10">
          <Stat icon={Calendar} label="Bookings" value={bookings.length} testid="portal-stat-bookings" />
          <Stat icon={FileText} label="Open Invoices" value={invoices.filter(i => i.status !== "paid").length} testid="portal-stat-invoices" />
          <Stat icon={CreditCard} label="Payments Made" value={payments.length} testid="portal-stat-payments" />
        </div>

        <div className="flex gap-1 border-b border-[var(--border)] mb-6">
          {[["bookings", "Bookings"], ["invoices", "Invoices"], ["payments", "Payments"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`portal-tab-${k}`}
              className={`px-5 py-3 text-sm font-medium transition relative ${tab === k ? "text-white" : "text-[var(--text-2)] hover:text-white"}`}>
              {l}
              {tab === k && <span className="absolute -bottom-px inset-x-0 h-px bg-[var(--red)]" />}
            </button>
          ))}
        </div>

        {loading ? <div className="text-[var(--text-2)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
          <>
            {tab === "bookings" && (
              <div className="space-y-3" data-testid="portal-bookings">
                {bookings.length === 0 && <Empty cta={<Link className="btn-red" to="/book-consultation">Book a Consultation</Link>}>No bookings yet.</Empty>}
                {bookings.map((b) => (
                  <div key={b.id} className="card-dark p-5 grid sm:grid-cols-5 gap-3 items-center" data-testid={`portal-booking-${b.id}`}>
                    <div className="sm:col-span-2">
                      <div className="font-display font-semibold">{b.topic}</div>
                      <div className="text-xs text-[var(--text-3)] uppercase tracking-[0.18em] mt-1">{b.type === "paid_in_person" ? "In-person · $250" : "Free virtual"}</div>
                    </div>
                    <div className="text-sm text-[var(--text-2)]">{b.preferred_date}<br/><span className="text-xs">{b.preferred_time}</span></div>
                    <Badge value={b.status} />
                    <Badge value={b.payment_status} />
                  </div>
                ))}
              </div>
            )}
            {tab === "invoices" && (
              <div className="space-y-3" data-testid="portal-invoices">
                {invoices.length === 0 && <Empty>No invoices yet.</Empty>}
                {invoices.map((i) => (
                  <div key={i.id} className="card-dark p-5 grid sm:grid-cols-5 gap-3 items-center" data-testid={`portal-invoice-${i.id}`}>
                    <div className="sm:col-span-2">
                      <div className="font-display font-semibold">{i.description}</div>
                      <div className="text-xs text-[var(--text-3)] mt-1">Due {i.due_date || "—"}</div>
                    </div>
                    <div className="font-display text-xl">${Number(i.amount).toLocaleString(undefined,{minimumFractionDigits:2})}</div>
                    <Badge value={i.status} />
                    <div>
                      {i.status === "paid"
                        ? <span className="text-emerald-400 text-sm">Paid</span>
                        : <button onClick={() => payInvoice(i)} disabled={busyInv===i.id} className="btn-red !py-2 !px-4 text-sm" data-testid={`pay-invoice-${i.id}`}>
                            {busyInv===i.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay invoice"}
                          </button>
                      }
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab === "payments" && (
              <div className="space-y-3" data-testid="portal-payments">
                {payments.length === 0 && <Empty>No payments yet.</Empty>}
                {payments.map((p) => (
                  <div key={p.id} className="card-dark p-5 grid sm:grid-cols-4 gap-3 items-center text-sm">
                    <div className="font-display capitalize">{p.kind}</div>
                    <div className="text-[var(--text-2)]">{new Date(p.created_at).toLocaleString()}</div>
                    <div className="font-display text-lg">${Number(p.amount).toFixed(2)}</div>
                    <Badge value={p.payment_status} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-dark p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-10 h-10 bg-[var(--surface-hover)] flex items-center justify-center"><Icon className="w-5 h-5 text-[var(--red)]" /></div>
      <div>
        <div className="font-display text-2xl font-semibold">{value}</div>
        <div className="text-xs text-[var(--text-3)] uppercase tracking-[0.2em]">{label}</div>
      </div>
    </div>
  );
}

function Badge({ value }) {
  const v = String(value || "").toLowerCase();
  const color = badgeColor(v);
  return <span className={`inline-flex items-center px-2.5 py-1 border text-[10px] uppercase tracking-[0.18em] ${color}`}>{value || "—"}</span>;
}

function badgeColor(v) {
  if (v === "paid" || v === "confirmed") return "text-emerald-400 border-emerald-400/40";
  if (v === "pending" || v === "pending_payment") return "text-yellow-300 border-yellow-300/40";
  if (v === "open") return "text-[var(--silver)] border-[var(--silver)]/40";
  return "text-[var(--text-2)] border-[var(--border)]";
}

function Empty({ children, cta }) {
  return (
    <div className="card-dark p-12 text-center">
      <div className="text-[var(--text-2)] mb-4">{children}</div>
      {cta}
    </div>
  );
}
